import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths } from 'date-fns';
import { de } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import CalendarService from '../../services/CalendarService';
import { CalendarSlot } from '../../types/calendar.types';
import ImprovedBookingModal from './ImprovedBookingModal';
import BookingSuccessModal from './BookingSuccessModal';
import SlotInquiryModal from './SlotInquiryModal';
import './ImprovedBookingCalendar.css';

interface GroupedSlots {
  [date: string]: CalendarSlot[];
}

// Calculate Easter Sunday using the Anonymous Gregorian algorithm
const getEasterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

// Get German holidays for Bayern (Bavaria) for a given year
const getGermanHolidays = (year: number): Set<string> => {
  const holidays = new Set<string>();

  // Fixed holidays
  holidays.add(`${year}-01-01`); // Neujahr
  holidays.add(`${year}-01-06`); // Heilige Drei Könige (Bayern)
  holidays.add(`${year}-05-01`); // Tag der Arbeit
  holidays.add(`${year}-08-15`); // Mariä Himmelfahrt (Bayern)
  holidays.add(`${year}-10-03`); // Tag der Deutschen Einheit
  holidays.add(`${year}-11-01`); // Allerheiligen (Bayern)
  holidays.add(`${year}-12-25`); // 1. Weihnachtsfeiertag
  holidays.add(`${year}-12-26`); // 2. Weihnachtsfeiertag

  // Easter-based holidays
  const easter = getEasterSunday(year);

  // Karfreitag (Good Friday) - Easter - 2 days
  const karfreitag = new Date(easter);
  karfreitag.setDate(easter.getDate() - 2);
  holidays.add(format(karfreitag, 'yyyy-MM-dd'));

  // Ostermontag (Easter Monday) - Easter + 1 day
  const ostermontag = new Date(easter);
  ostermontag.setDate(easter.getDate() + 1);
  holidays.add(format(ostermontag, 'yyyy-MM-dd'));

  // Christi Himmelfahrt (Ascension) - Easter + 39 days
  const himmelfahrt = new Date(easter);
  himmelfahrt.setDate(easter.getDate() + 39);
  holidays.add(format(himmelfahrt, 'yyyy-MM-dd'));

  // Pfingstmontag (Whit Monday) - Easter + 50 days
  const pfingstmontag = new Date(easter);
  pfingstmontag.setDate(easter.getDate() + 50);
  holidays.add(format(pfingstmontag, 'yyyy-MM-dd'));

  // Fronleichnam (Corpus Christi) - Easter + 60 days (Bayern)
  const fronleichnam = new Date(easter);
  fronleichnam.setDate(easter.getDate() + 60);
  holidays.add(format(fronleichnam, 'yyyy-MM-dd'));

  return holidays;
};

interface BookingSuccessData {
  bookingId: number;
  bookingNumber?: string;
  scheduleDate: string;
  scheduleTime: string;
  tourType: string;
  participantCount: number;
  totalAmount: number;
  voucherCode?: string;
  voucherDiscount?: number;
}

const ImprovedBookingCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [slots, setSlots] = useState<CalendarSlot[]>([]);
  const [groupedSlots, setGroupedSlots] = useState<GroupedSlots>({});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<CalendarSlot | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<BookingSuccessData | null>(null);

  // Load slots for current month
  useEffect(() => {
    loadMonthSlots();
  }, [currentMonth]);

  // Group slots by date whenever slots change
  useEffect(() => {
    const grouped = slots.reduce((acc: GroupedSlots, slot) => {
      if (!acc[slot.date]) {
        acc[slot.date] = [];
      }
      acc[slot.date].push(slot);
      return acc;
    }, {});

    // Sort slots within each day by time
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => a.time.localeCompare(b.time));
    });

    setGroupedSlots(grouped);
  }, [slots]);

  // Group slots by time for display (merge same-time variants)
  // WICHTIG: Alle Tour-Typen teilen sich die Kapazität im selben Timeslot!
  const groupSlotsByTime = (daySlots: CalendarSlot[]) => {
    const timeGroups: { [time: string]: CalendarSlot[] } = {};

    daySlots.forEach(slot => {
      if (!timeGroups[slot.time]) {
        timeGroups[slot.time] = [];
      }
      timeGroups[slot.time].push(slot);
    });

    // Convert to array and sort by time
    return Object.entries(timeGroups)
      .sort(([timeA], [timeB]) => timeA.localeCompare(timeB))
      .map(([time, slotsAtTime]) => {
        // TIMESLOT-KAPAZITÄT: Alle Tour-Typen teilen sich die Buchungen
        // currentBookings ist identisch für alle Slots im Timeslot (Backend liefert Timeslot-Gesamtwert)
        // Nehme den MAXIMALEN verfügbaren Platz aus allen Tour-Typen
        const maxAvailable = Math.max(...slotsAtTime.map(s => s.availableCapacity));
        // Die höchste MaxKapazität im Timeslot
        const highestMax = Math.max(...slotsAtTime.map(s => s.maxCapacity));

        return {
          time,
          slots: slotsAtTime,
          // KORREKT: Zeige den maximalen verfügbaren Platz (nicht summieren!)
          totalAvailable: maxAvailable,
          totalMax: highestMax,
          // Use the first slot's data for common properties
          isAvailable: slotsAtTime.some(s => s.isAvailable && !s.isBlocked && s.availableCapacity > 0),
          // Befund B7: Timeslot gilt als "geplant", wenn KEIN Slot freigegeben ist
          // (alle Slots eines Timeslots teilen sich den Schedule und damit den Freigabestatus)
          isPlanned: slotsAtTime.every(s => s.isReleasedForOnlineBooking === false)
        };
      });
  };

  const loadMonthSlots = async () => {
    setLoading(true);
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      // Befund B7: geplante, noch nicht freigegebene Termine mitladen
      // (isReleasedForOnlineBooking === false), sie sind sichtbar und anfragbar, aber nicht buchbar.
      const data = await CalendarService.getAvailableSlots(start, end, undefined, true);
      setSlots(data);
    } catch (err) {
      console.error('Error loading slots:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    const firstDayOfWeek = (start.getDay() + 6) % 7;
    const emptyDays = Array(firstDayOfWeek).fill(null);
    return [...emptyDays, ...days];
  };

  const getSlotColor = (slot: CalendarSlot): string => {
    if (!slot.isAvailable || slot.isBlocked) return '#999';

    const availabilityPercent = (slot.availableCapacity / slot.maxCapacity) * 100;

    if (availabilityPercent === 0) return '#dc2626'; // Red - Full
    if (availabilityPercent <= 20) return '#f59e0b'; // Orange - Almost full
    if (availabilityPercent <= 50) return '#84cc16'; // Yellow - Half full
    return '#059669'; // Green - Available
  };

  const getTimeGroupColor = (totalAvailable: number, totalMax: number, isPast: boolean): string => {
    // Past slots are always gray
    if (isPast) return '#999';

    if (totalAvailable === 0) return '#dc2626'; // Red - Full

    const availabilityPercent = (totalAvailable / totalMax) * 100;

    if (availabilityPercent <= 20) return '#f59e0b'; // Orange - Almost full
    if (availabilityPercent <= 50) return '#84cc16'; // Yellow - Half full
    return '#059669'; // Green - Available
  };

  const isSlotInPast = (dateStr: string, time: string): boolean => {
    const now = new Date();
    const slotDate = new Date(dateStr);

    // Parse time (format: "HH:mm")
    const [hours, minutes] = time.split(':').map(Number);
    slotDate.setHours(hours, minutes, 0, 0);

    return slotDate < now;
  };

  const getTourTypeName = (tourType: string): string => {
    const names: { [key: string]: string } = {
      'STANDARD': 'Standard',
      'ALL_IN': 'All-In',
      'ALL_IN_PREMIUM': 'All-In Premium',
      'EXCLUSIVE': 'Exklusiv',
      'WEDDING': 'Hochzeit'
    };
    return names[tourType] || tourType;
  };

  // Format time: "11:00:00" → "11:00"
  const formatTime = (time: string): string => {
    const parts = time.split(':');
    return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
  };

  // Format capacity: Show available capacity per variant separated by "/"
  // e.g., 2 variants with 140 and 50 free → "140/50"
  // e.g., 3 variants with 140, 50, 110 free → "140/50/110"
  const formatCapacity = (slots: CalendarSlot[]): string => {
    if (slots.length === 0) return '0';

    // Sort by tourType for consistent order
    const sortedSlots = [...slots].sort((a, b) => a.tourType.localeCompare(b.tourType));

    // Get available capacity for each variant
    const capacities = sortedSlots.map(s => s.availableCapacity);

    return capacities.join('/');
  };

  const handleSlotClick = (slot: CalendarSlot) => {
    // Check if slot is in the past
    const isPast = isSlotInPast(slot.date, slot.time);
    if (isPast) {
      return; // Don't allow booking past slots
    }

    // Befund B7: geplante, noch nicht freigegebene Termine öffnen das Anfrage-Formular
    // statt der Buchung (buchbar sind sie serverseitig ohnehin nicht).
    if (slot.isReleasedForOnlineBooking === false) {
      setSelectedSlot(slot);
      setShowInquiryModal(true);
      return;
    }

    if (slot.isAvailable && !slot.isBlocked && slot.availableCapacity > 0) {
      setSelectedSlot(slot);
      setShowBookingModal(true);
    }
  };

  const handleBookingSuccess = async (bookingId: number, voucherCode?: string, voucherDiscount?: number) => {
    setShowBookingModal(false);

    // Fetch booking details from API
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/bookings/${bookingId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const booking = await response.json();
        const subtotal = booking.pricing?.totalGross || booking.totalPrice || 0;
        const discount = voucherDiscount || 0;
        setSuccessData({
          bookingId: booking.id,
          bookingNumber: booking.bookingNumber,
          scheduleDate: booking.scheduleDate || selectedSlot?.date || '',
          scheduleTime: booking.startTime || selectedSlot?.time || '',
          tourType: selectedSlot?.tourType || 'Standard',
          participantCount: booking.participantCount || 0,
          totalAmount: Math.max(0, subtotal - discount),
          voucherCode,
          voucherDiscount: discount
        });
        setShowSuccessModal(true);
      } else {
        // Fallback: Use selected slot data if API fails
        setSuccessData({
          bookingId,
          scheduleDate: selectedSlot?.date || '',
          scheduleTime: selectedSlot?.time || '',
          tourType: selectedSlot?.tourType || 'Standard',
          participantCount: 0,
          totalAmount: 0,
          voucherCode,
          voucherDiscount
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      // Fallback: Use selected slot data
      setSuccessData({
        bookingId,
        scheduleDate: selectedSlot?.date || '',
        scheduleTime: selectedSlot?.time || '',
        tourType: selectedSlot?.tourType || 'Standard',
        participantCount: 0,
        totalAmount: 0,
        voucherCode,
        voucherDiscount
      });
      setShowSuccessModal(true);
    }

    setSelectedSlot(null);
    loadMonthSlots(); // Reload to show updated capacity
  };

  const handlePayNow = () => {
    if (successData) {
      const params = new URLSearchParams();
      if (successData.voucherCode) {
        params.set('voucherCode', successData.voucherCode);
      }
      if (successData.voucherDiscount) {
        params.set('voucherDiscount', successData.voucherDiscount.toString());
      }
      const queryString = params.toString();
      navigate(`/checkout/${successData.bookingId}${queryString ? `?${queryString}` : ''}`);
    }
  };

  const handleViewBooking = () => {
    if (successData) {
      const params = new URLSearchParams();
      if (successData.voucherCode) {
        params.set('voucherCode', successData.voucherCode);
      }
      if (successData.voucherDiscount) {
        params.set('voucherDiscount', successData.voucherDiscount.toString());
      }
      const queryString = params.toString();
      navigate(`/booking-confirmation/${successData.bookingId}${queryString ? `?${queryString}` : ''}`);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    setSuccessData(null);
  };

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  return (
    <div className="improved-booking-calendar">
      <div className="calendar-header">
        <button onClick={handlePrevMonth} className="month-nav-btn">
          ←
        </button>
        <h2 className="month-title">
          {format(currentMonth, 'MMMM yyyy', { locale: de })}
        </h2>
        <button onClick={handleNextMonth} className="month-nav-btn">
          →
        </button>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="weekdays-header">
        {['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'].map(day => (
          <div key={day} className="weekday">{day}</div>
        ))}
      </div>

      <div className="calendar-grid">
        {getDaysInMonth().map((date, idx) => {
          if (!date) {
            return <div key={`empty-${idx}`} className="calendar-day empty" />;
          }

          const dateStr = format(date, 'yyyy-MM-dd');
          const daySlots = groupedSlots[dateStr] || [];
          const timeGroups = groupSlotsByTime(daySlots);
          const isToday = format(new Date(), 'yyyy-MM-dd') === dateStr;
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const isSelected = selectedDate === dateStr;
          const holidays = getGermanHolidays(date.getFullYear());
          const isHoliday = holidays.has(dateStr);

          return (
            <div
              key={idx}
              className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => setSelectedDate(dateStr)}
            >
              <div className={`day-number ${isWeekend ? 'weekend' : ''} ${isHoliday ? 'holiday' : ''}`}>
                {format(date, 'd')}
              </div>

              {timeGroups.length > 0 && (
                <div className="timeslots">
                  {timeGroups.map((timeGroup, tgIdx) => {
                    const isPast = isSlotInPast(dateStr, timeGroup.time);
                    const isPlanned = !isPast && timeGroup.isPlanned;
                    const baseColor = isPlanned ? '#2563eb' : getTimeGroupColor(timeGroup.totalAvailable, timeGroup.totalMax, isPast);

                    // Create lighter and darker versions for stronger gradient
                    let lighterColor, darkerColor;
                    if (isPast) {
                      // Special gray gradient for past slots - blasser
                      lighterColor = '#d0d0d0'; // Very light gray
                      darkerColor = '#9a9a9a'; // Medium gray
                    } else {
                      // More intense colors for active slots
                      lighterColor = baseColor + '80'; // 50% opacity for lighter start (was 99/60%)
                      darkerColor = baseColor; // Full opacity for darker end
                    }

                    const title = isPast
                      ? `${formatTime(timeGroup.time)} Uhr - Vergangen`
                      : isPlanned
                        ? `${formatTime(timeGroup.time)} Uhr - Termin in Planung, jetzt anfragen`
                        : `${formatTime(timeGroup.time)} Uhr - ${timeGroup.totalAvailable} Plätze frei`;

                    return (
                      <div
                        key={`${dateStr}-${timeGroup.time}-${tgIdx}`}
                        className={`timeslot ${timeGroup.totalAvailable === 0 ? 'full' : ''} ${isPast ? 'past' : ''} ${isPlanned ? 'planned' : ''}`}
                        style={{
                          background: `linear-gradient(135deg, ${lighterColor} 0%, ${darkerColor} 100%)`,
                          borderColor: isPast ? '#808080' : baseColor
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isPast) {
                            // Click on first available slot in group
                            const firstAvailableSlot = timeGroup.slots.find(s => s.availableCapacity > 0) || timeGroup.slots[0];
                            handleSlotClick(firstAvailableSlot);
                          }
                        }}
                        title={title}
                      >
                        <span className="timeslot-time">{formatTime(timeGroup.time)} Uhr</span>
                        <span className="timeslot-capacity">
                          {isPast ? 'Vergangen' : isPlanned ? 'auf Anfrage' : formatCapacity(timeGroup.slots)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {timeGroups.length === 0 && (
                <div className="no-slots">Keine Termine</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#059669' }}></span>
          <span>Verfügbar</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#84cc16' }}></span>
          <span>Wenige Plätze</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#f59e0b' }}></span>
          <span>Fast ausgebucht</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#dc2626' }}></span>
          <span>Ausgebucht</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#2563eb' }}></span>
          <span>In Planung, auf Anfrage</span>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedSlot && (
        <ImprovedBookingModal
          slot={selectedSlot}
          availableSlotsForDay={(groupedSlots[selectedSlot.date] || [selectedSlot])
            .filter(s => s.isReleasedForOnlineBooking !== false) /* Befund B7: geplante Termine nicht im Buchungsdialog anbieten */}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedSlot(null);
          }}
          onSuccess={handleBookingSuccess}
        />
      )}

      {/* Terminanfrage-Modal für geplante, noch nicht freigegebene Termine (Befund B7) */}
      {showInquiryModal && selectedSlot && (
        <SlotInquiryModal
          slot={selectedSlot}
          onClose={() => {
            setShowInquiryModal(false);
            setSelectedSlot(null);
          }}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && successData && (
        <BookingSuccessModal
          bookingId={successData.bookingId}
          bookingNumber={successData.bookingNumber}
          scheduleDate={successData.scheduleDate}
          scheduleTime={successData.scheduleTime}
          tourType={successData.tourType}
          participantCount={successData.participantCount}
          totalAmount={successData.totalAmount}
          voucherCode={successData.voucherCode}
          voucherDiscount={successData.voucherDiscount}
          onClose={handleCloseSuccess}
          onPayNow={handlePayNow}
          onViewBooking={handleViewBooking}
        />
      )}
    </div>
  );
};

export default ImprovedBookingCalendar;
